// Rona Wellness — Brand tokens
export const COLORS = {
  // Core palette (fixed always)
  sage: "#8DA290",
  sageDeep: "#6F8778",
  gold: "#B8935A",
  goldLight: "#D4B079",
  goldDeep: "#8E6B3A",
  brown: "#8A6A4A",
  brownDeep: "#6B4F35",
  dustyBlue: "#8AA0B0",
  dustyBlueDeep: "#5F7688",
  cream: "#F5EFE4",
  creamSoft: "#FBF7EE",
  sandBeige: "#D6C4A8",
  yellow: "#F2D78A",
  yellowSoft: "#FCECB5",
  ink: "#2D2A24",
  inkSoft: "#5A5449",
};

// Rona Wellness — parent brand tagline
export const BRAND_TAGLINE = "Where Nature Meets the Soul";
// The Rona Walk — concept sub-brand tagline
export const WALK_TAGLINE = "Where Movement Meets Peace";
// Backwards-compat alias
export const TAGLINE = BRAND_TAGLINE;

export const PILLARS = "Faith  •  Strength  •  Hope";

// Tagline hierarchy — 6 levels
export const TAGLINE_HIERARCHY = [
  { level: "Primary · parent brand", phrase: "Where Nature Meets the Soul.", where: "Website, banners, packaging — everything default" },
  { level: "Concept · sub-brand", phrase: "Where Movement Meets Peace.", where: "The Rona Walk logo, community events" },
  { level: "Invitation · opening call", phrase: "Take one gentle step.", where: "Landing page welcome, first-time-visitor overlays, gentle CTAs" },
  { level: "Product mantra", phrase: "Walk · Hydrate · Thrive.", where: "Water bottles, journals, wellness accessories" },
  { level: "Social mantra", phrase: "Soft healing is still healing.", where: "Instagram quote cards, story overlays" },
  { level: "Rally cry", phrase: "There is healing in movement.", where: "Community posts, YouTube intros" },
];

// Seasonal accent palettes — rotate for campaigns; core palette stays fixed
export const SEASONAL_PALETTES = [
  {
    id: "spring",
    name: "Spring",
    subtitle: "Mental Health Awareness · Renewal",
    months: "March – May",
    accents: [
      { name: "Soft Lavender", hex: "#B8A6C4", dark: true },
      { name: "Dusty Blue (hero)", hex: "#8AA0B0", dark: true },
      { name: "Sage", hex: "#8DA290", dark: true },
    ],
  },
  {
    id: "summer",
    name: "Summer",
    subtitle: "Friends · Family · Joyful Moments",
    months: "June – August",
    accents: [
      { name: "Warm Coral", hex: "#E8A187", dark: false },
      { name: "Fresh Sage", hex: "#A6BBA1", dark: false },
      { name: "Soft Gold", hex: "#D4B079", dark: false },
    ],
  },
  {
    id: "autumn",
    name: "Autumn",
    subtitle: "The Reflection · Self-Care Walks",
    months: "September – November",
    accents: [
      { name: "Terracotta", hex: "#B5673C", dark: true },
      { name: "Sienna", hex: "#8E4A2A", dark: true },
      { name: "Deep Sage", hex: "#6F8778", dark: true },
    ],
  },
  {
    id: "winter",
    name: "Winter",
    subtitle: "Christmas · New Year · Sacred Stillness",
    months: "December – January",
    accents: [
      { name: "Forest Green", hex: "#3E5D46", dark: true },
      { name: "Burgundy", hex: "#7A2E3A", dark: true },
      { name: "Rich Gold", hex: "#B8935A", dark: true },
    ],
  },
  {
    id: "feb",
    name: "February",
    subtitle: "Day & Night Inspiration",
    months: "February only",
    accents: [
      { name: "Day · Dusty Blue", hex: "#8AA0B0", dark: true },
      { name: "Night · Midnight", hex: "#1F2A44", dark: true },
      { name: "Star Gold", hex: "#D4B079", dark: false },
    ],
  },
];

// Brand stories — purified
export const STORY_WELLNESS = {
  title: "Rona Wellness",
  lines: [
    "Rona Wellness was born from a quiet belief — that healing does not always begin with dramatic change. It often begins with one gentle step.",
    "In a fast-moving world, Rona Wellness invites people back to themselves through mindful movement, nature, walking, reflection, connection, and inner peace.",
    "Rona Wellness is a luxury lifestyle brand — walking for wellness. A soft path for anyone seeking inner strength, peace, and calm healing through walking meditation and mindful living.",
    "Built on three pillars — Faith · Strength · Hope — with a special heart for the working woman who feels overwhelmed, and for the friends and family who walk beside her.",
    "It is not simply a product brand. It is a lifestyle movement — for spiritual grounding, quiet self-care, and intentional living.",
  ],
  close: "Where Nature Meets the Soul.",
};

// Brand Mission, Vision, Values
export const BRAND_MISSION =
  "To inspire healing, mindful movement, inner peace, hope, and connection — through wellness products, nature, walking, community, and positive living.";

export const BRAND_VISION =
  "To become a globally recognized wellness lifestyle brand that encourages people to reconnect with themselves, nature, and each other — through movement, reflection, and intentional living.";

export const BRAND_VALUES = [
  "Hope",
  "Wellness",
  "Connection",
  "Authenticity",
  "Growth",
  "Grounding",
  "Community",
];

// Seasonal collection branding system — for The Rona Walk page
export const SEASONS = [
  {
    id: "spring",
    name: "Spring",
    theme: "Awaken",
    title: "The Mindful Community Walk",
    message: "New beginnings.",
    events: "May Day · Mental Health Awareness · Easter",
    hashtag: "#WalkWithKindness",
    icon: "tree",
    treeState: "Branches with light, delicate blooming leaf buds and tiny flowers.",
    accent: "#B8A6C4",
  },
  {
    id: "summer",
    name: "Summer",
    theme: "Thrive",
    title: "Friendship & Family Walk",
    message: "Growth and vitality.",
    events: "Summer solstice · Community walks",
    hashtag: "#WalkWithRona",
    icon: "tree",
    treeState: "Canopy full, lush, and rich with mature summer leaves.",
    accent: "#E8A187",
  },
  {
    id: "autumn",
    name: "Autumn",
    theme: "Grounding",
    title: "The Reflection & Gratitude",
    message: "Autumn walks heal softly. Meditation walks for burnout, for busy women.",
    events: "Harvest · Thanksgiving",
    hashtag: "#WalkWithGratitude",
    icon: "tree",
    treeState: "Branches with minimal leaves softly falling toward the path.",
    accent: "#B5673C",
  },
  {
    id: "winter",
    name: "Winter",
    theme: "Restore",
    title: "Rest & Renewal",
    message: "A season to breathe, restore, and renew.",
    events: "Christmas · New Year",
    hashtag: "#WinterRestore",
    icon: "tree",
    treeState: "Bare winter branches, quiet path, snow-hushed landscape.",
    accent: "#3E5D46",
  },
  {
    id: "feb",
    name: "February · Day & Night",
    theme: "Balance",
    title: "Morning & Evening Meditation Walks",
    message: "Day needs light, and night needs rest. But we need both.",
    events: "Day & Night Inspiration month",
    hashtag: "#DayAndNight",
    icon: "sun-moon",
    treeState: "Sun and Moon — twin icons of light and rest.",
    accent: "#1F2A44",
  },
];

// Site config for ronawellness.shop
export const SITE = {
  domain: "ronawellness.shop",
  email: "contact@ronawellness.shop",
  emailFounder: "robinahallgaier@ronawellness.shop",
  emailSupport: "support@ronawellness.shop",
  socials: {
    instagram: "https://www.instagram.com/walkwithrona",
    tiktok: "https://www.tiktok.com/@walkwithrona",
    youtube: "https://www.youtube.com/@walkwithrona",
    pinterest: "https://pinterest.com/walkwithrona",
    linkedin: "https://www.linkedin.com/company/rona-wellness",
  },
  shops: {
    etsy: "https://ronawellness.etsy.com",
    tapstitch: "https://tapstitch.com/RonaWellness",
  },
};

export const STORY_WALK = {
  title: "The Rona Walk",
  intro: "Where Movement Meets Peace.",
  lines: [
    "There was a time when life felt heavy — physically, mentally, emotionally. My thoughts were loud. My energy was low.",
    "And so, one quiet morning, I stepped outside. No pressure. No expectations. Just me, and the softness of the air.",
    "The silence felt safe. The air felt lighter. Something inside me began to change.",
    "The Rona Walk was born from that morning — and from every morning that followed. It is not exercise. It is not a routine to check off. It is a daily return to yourself — slowly, gently, and honestly.",
    "More than a walk, the Rona Walk is a prayer, a therapy, a reset. A soft healing for body, mind, and soul.",
  ],
  close: "You don't have to rush life. You just have to keep moving.",
  mantra: "Walk · Hydrate · Thrive.",
  tips: [
    "Wear comfortable shoes and comfortable clothes",
    "Choose peaceful routes — parks, forest paths, quiet streets",
    "Stretch lightly before and after",
    "Stay hydrated",
    "Enjoy the movement. Don't rush.",
  ],
};
